/**
 * PartyHUD - Top-left 6-person party status frame.
 * High-performance cached DOM architecture (Eliminates GC pause & freezing).
 * Displays real-time HP/SP/MP, Concentration, Cast bars, and Buffs.
 */

import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';

interface CardElements {
  cardEl: HTMLElement;
  avatarEl: HTMLElement;
  nameEl: HTMLElement;
  jobEl: HTMLElement;
  leaderBadgeEl: HTMLElement;
  switchBtnEl: HTMLElement;
  hpBarEl: HTMLElement;
  spBarEl: HTMLElement;
  mpBarEl: HTMLElement;
  expBarEl: HTMLElement;
  concEl: HTMLElement;
  castBoxEl: HTMLElement;
  castBarEl: HTMLElement;
  castTextEl: HTMLElement;
  buffsEl: HTMLElement;
  // Tooltip tracking closures or updaters
  updateTooltips?: (member: EntityState) => void;
}

export class PartyHUD {
  private containerEl: HTMLElement | null = null;
  private onSelectAlly: (id: string) => void;
  private onSwitchPlayer?: (id: string) => void;
  private cachedCards: Record<string, CardElements> = {};

  constructor(onSelectAlly: (id: string) => void, onSwitchPlayer?: (id: string) => void) {
    this.onSelectAlly = onSelectAlly;
    this.onSwitchPlayer = onSwitchPlayer;
  }

  public mount(parent: HTMLElement): void {
    const el = document.createElement('div');
    el.id = 'party-hud';
    el.style.position = 'absolute';
    el.style.left = '16px';
    el.style.top = '52px';
    el.style.width = '240px';
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.gap = '6px';
    el.style.zIndex = '50';
    el.style.userSelect = 'none';

    parent.appendChild(el);
    this.containerEl = el;
  }

  private ensureCard(member: EntityState, index: number): CardElements {
    if (this.cachedCards[member.id]) return this.cachedCards[member.id];

    const cardEl = document.createElement('div');
    cardEl.style.background = 'rgba(13, 17, 23, 0.85)';
    cardEl.style.border = '1.5px solid #30363d';
    cardEl.style.borderRadius = '6px';
    cardEl.style.padding = '6px 8px';
    cardEl.style.cursor = 'pointer';
    cardEl.style.transition = 'border-color 0.15s, background-color 0.15s';
    cardEl.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';

    // Click: select target
    cardEl.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onSelectAlly(member.id);
    });

    // Dblclick: switch player
    cardEl.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (this.onSwitchPlayer) this.onSwitchPlayer(member.id);
    });

    // Header row
    const headRow = document.createElement('div');
    headRow.style.display = 'flex';
    headRow.style.justifyContent = 'space-between';
    headRow.style.alignItems = 'center';
    headRow.style.marginBottom = '4px';

    const leftGroup = document.createElement('div');
    leftGroup.style.display = 'flex';
    leftGroup.style.alignItems = 'center';
    leftGroup.style.gap = '6px';

    const avatarEl = document.createElement('span');
    avatarEl.style.fontSize = '14px';
    avatarEl.innerText = member.avatarIcon;
    leftGroup.appendChild(avatarEl);

    const nameEl = document.createElement('span');
    nameEl.style.fontSize = '12px';
    nameEl.style.fontWeight = 'bold';
    nameEl.style.color = '#f1f5f9';
    nameEl.innerText = member.name;
    leftGroup.appendChild(nameEl);

    const jobEl = document.createElement('span');
    jobEl.style.fontSize = '10px';
    jobEl.style.color = '#94a3b8';
    jobEl.innerText = member.job;
    leftGroup.appendChild(jobEl);

    const leaderBadgeEl = document.createElement('span');
    leaderBadgeEl.style.fontSize = '9px';
    leaderBadgeEl.style.background = '#f59e0b';
    leaderBadgeEl.style.color = '#000';
    leaderBadgeEl.style.fontWeight = 'bold';
    leaderBadgeEl.style.padding = '1px 4px';
    leaderBadgeEl.style.borderRadius = '3px';
    leaderBadgeEl.style.display = 'none';
    leaderBadgeEl.innerText = '👑 LEADER';
    leftGroup.appendChild(leaderBadgeEl);

    headRow.appendChild(leftGroup);

    const rightGroup = document.createElement('div');
    rightGroup.style.display = 'flex';
    rightGroup.style.alignItems = 'center';
    rightGroup.style.gap = '4px';

    const switchBtnEl = document.createElement('button');
    switchBtnEl.style.background = '#1e293b';
    switchBtnEl.style.border = '1px solid #475569';
    switchBtnEl.style.color = '#fcd34d';
    switchBtnEl.style.fontSize = '10px';
    switchBtnEl.style.borderRadius = '3px';
    switchBtnEl.style.padding = '1px 5px';
    switchBtnEl.style.cursor = 'pointer';
    switchBtnEl.innerText = '👑 조작';
    switchBtnEl.title = '이 캐릭터로 직접 조작 전환';
    switchBtnEl.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.onSwitchPlayer) this.onSwitchPlayer(member.id);
    });
    rightGroup.appendChild(switchBtnEl);

    const fKeyEl = document.createElement('span');
    fKeyEl.style.fontSize = '10px';
    fKeyEl.style.fontWeight = 'bold';
    fKeyEl.style.color = '#f59e0b';
    fKeyEl.style.background = 'rgba(245,158,11,0.15)';
    fKeyEl.style.padding = '1px 4px';
    fKeyEl.style.borderRadius = '3px';
    fKeyEl.innerText = `F${index + 1}`;
    rightGroup.appendChild(fKeyEl);

    headRow.appendChild(rightGroup);
    cardEl.appendChild(headRow);

    // 1. HP Bar (두께 8px로 키움)
    const hpTrack = document.createElement('div');
    hpTrack.style.position = 'relative';
    hpTrack.style.width = '100%';
    hpTrack.style.background = '#1e293b';
    hpTrack.style.border = '1px solid #334155';
    hpTrack.style.height = '8px';
    hpTrack.style.borderRadius = '3px';
    hpTrack.style.overflow = 'hidden';
    hpTrack.style.marginBottom = '3px';
    hpTrack.style.cursor = 'help';

    const hpBarEl = document.createElement('div');
    hpBarEl.style.height = '100%';
    hpBarEl.style.background = '#22c55e';
    hpBarEl.style.transition = 'width 0.1s ease';
    hpTrack.appendChild(hpBarEl);
    cardEl.appendChild(hpTrack);

    // 2. SP Bar (두께 8px로 키움)
    const spTrack = document.createElement('div');
    spTrack.style.position = 'relative';
    spTrack.style.width = '100%';
    spTrack.style.background = '#1e293b';
    spTrack.style.border = '1px solid #334155';
    spTrack.style.height = '8px';
    spTrack.style.borderRadius = '3px';
    spTrack.style.overflow = 'hidden';
    spTrack.style.marginBottom = '3px';
    spTrack.style.cursor = 'help';

    const spBarEl = document.createElement('div');
    spBarEl.style.height = '100%';
    spBarEl.style.background = '#eab308';
    spBarEl.style.transition = 'width 0.1s ease';
    spTrack.appendChild(spBarEl);
    cardEl.appendChild(spTrack);

    // 3. MP Bar (두께 8px로 키움)
    const mpTrack = document.createElement('div');
    mpTrack.style.position = 'relative';
    mpTrack.style.width = '100%';
    mpTrack.style.background = '#1e293b';
    mpTrack.style.border = '1px solid #334155';
    mpTrack.style.height = '8px';
    mpTrack.style.borderRadius = '3px';
    mpTrack.style.overflow = 'hidden';
    mpTrack.style.marginBottom = '3px';
    mpTrack.style.cursor = 'help';

    const mpBarEl = document.createElement('div');
    mpBarEl.style.height = '100%';
    mpBarEl.style.background = '#3b82f6';
    mpBarEl.style.transition = 'width 0.1s ease';
    mpTrack.appendChild(mpBarEl);
    cardEl.appendChild(mpTrack);

    // 4. EXP Bar (신규 추가, 두께 8px, 보라/자주색 그라데이션)
    const expTrack = document.createElement('div');
    expTrack.style.position = 'relative';
    expTrack.style.width = '100%';
    expTrack.style.background = '#1e293b';
    expTrack.style.border = '1px solid #334155';
    expTrack.style.height = '8px';
    expTrack.style.borderRadius = '3px';
    expTrack.style.overflow = 'hidden';
    expTrack.style.marginBottom = '3px';
    expTrack.style.cursor = 'help';

    const expBarEl = document.createElement('div');
    expBarEl.style.height = '100%';
    expBarEl.style.background = 'linear-gradient(90deg, #a855f7, #c084fc)';
    expBarEl.style.transition = 'width 0.1s ease';
    expTrack.appendChild(expBarEl);
    cardEl.appendChild(expTrack);

    // Float Tooltip Element (마우스 오버 / 클릭 시 수치 및 % 표시)
    const tooltipEl = document.createElement('div');
    tooltipEl.style.cssText =
      'display:none;position:absolute;z-index:9999;background:rgba(15,23,42,0.95);border:1px solid #f59e0b;color:#f8fafc;padding:3px 8px;border-radius:4px;font-size:11px;font-weight:bold;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.7);pointer-events:none;transform:translate(-50%, -125%);';
    document.body.appendChild(tooltipEl);

    let activeTooltipType: 'hp' | 'sp' | 'mp' | 'exp' | null = null;
    let latestMemberData: EntityState = member;

    const showTip = (type: 'hp' | 'sp' | 'mp' | 'exp', e: MouseEvent) => {
      activeTooltipType = type;
      updateTipText(type);
      tooltipEl.style.display = 'block';
      tooltipEl.style.left = `${e.pageX}px`;
      tooltipEl.style.top = `${e.pageY - 6}px`;
    };

    const updateTipText = (type: 'hp' | 'sp' | 'mp' | 'exp') => {
      const res = latestMemberData.resources;
      if (type === 'hp') {
        const cur = Math.round(res.hp);
        const max = Math.round(res.maxHp);
        const pct = ((res.hp / Math.max(1, res.maxHp)) * 100).toFixed(1);
        tooltipEl.innerHTML = `<span style="color:#22c55e;">❤️ HP:</span> ${cur} / ${max} <span style="color:#86efac;">(${pct}%)</span>`;
      } else if (type === 'sp') {
        const cur = Math.round(res.sp);
        const max = Math.round(res.maxSp);
        const pct = ((res.sp / Math.max(1, res.maxSp)) * 100).toFixed(1);
        tooltipEl.innerHTML = `<span style="color:#eab308;">⚡ SP (기력):</span> ${cur} / ${max} <span style="color:#fde047;">(${pct}%)</span>`;
      } else if (type === 'mp') {
        const cur = Math.round(res.mp);
        const max = Math.round(res.maxMp);
        const pct = ((res.mp / Math.max(1, res.maxMp)) * 100).toFixed(1);
        tooltipEl.innerHTML = `<span style="color:#38bdf8;">💧 MP (마나):</span> ${cur} / ${max} <span style="color:#93c5fd;">(${pct}%)</span>`;
      } else if (type === 'exp') {
        const cur = Math.round(latestMemberData.exp || 0);
        const max = Math.round(latestMemberData.maxExp || Math.max(100, (latestMemberData.level || 1) * 1000));
        const pct = ((cur / Math.max(1, max)) * 100).toFixed(1);
        const lvl = latestMemberData.level || 1;
        tooltipEl.innerHTML = `<span style="color:#c084fc;">🌟 EXP (Lv.${lvl}):</span> ${cur} / ${max} <span style="color:#e9d5ff;">(${pct}%)</span>`;
      }
    };

    const hideTip = () => {
      activeTooltipType = null;
      tooltipEl.style.display = 'none';
    };

    const bindTrackEvents = (el: HTMLElement, type: 'hp' | 'sp' | 'mp' | 'exp') => {
      el.addEventListener('mouseenter', (e) => showTip(type, e));
      el.addEventListener('mousemove', (e) => {
        tooltipEl.style.left = `${e.pageX}px`;
        tooltipEl.style.top = `${e.pageY - 6}px`;
      });
      el.addEventListener('mouseleave', hideTip);
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        showTip(type, e);
      });
    };

    bindTrackEvents(hpTrack, 'hp');
    bindTrackEvents(spTrack, 'sp');
    bindTrackEvents(mpTrack, 'mp');
    bindTrackEvents(expTrack, 'exp');

    // Conc info
    const concEl = document.createElement('div');
    concEl.style.fontSize = '10px';
    concEl.style.color = '#c084fc';
    concEl.style.display = 'none';
    concEl.style.justifyContent = 'space-between';
    concEl.style.marginTop = '2px';
    cardEl.appendChild(concEl);

    // Cast bar
    const castBoxEl = document.createElement('div');
    castBoxEl.style.marginTop = '4px';
    castBoxEl.style.background = '#1e293b';
    castBoxEl.style.borderRadius = '3px';
    castBoxEl.style.overflow = 'hidden';
    castBoxEl.style.height = '12px';
    castBoxEl.style.position = 'relative';
    castBoxEl.style.display = 'none';

    const castBarEl = document.createElement('div');
    castBarEl.style.width = '0%';
    castBarEl.style.background = '#38bdf8';
    castBarEl.style.height = '100%';
    castBoxEl.appendChild(castBarEl);

    const castTextEl = document.createElement('span');
    castTextEl.style.position = 'absolute';
    castTextEl.style.top = '0';
    castTextEl.style.left = '4px';
    castTextEl.style.fontSize = '9px';
    castTextEl.style.color = '#ffffff';
    castTextEl.style.fontWeight = 'bold';
    castTextEl.style.lineHeight = '12px';
    castBoxEl.appendChild(castTextEl);

    cardEl.appendChild(castBoxEl);

    // Buffs row
    const buffsEl = document.createElement('div');
    buffsEl.style.display = 'flex';
    buffsEl.style.gap = '3px';
    buffsEl.style.marginTop = '2px';
    cardEl.appendChild(buffsEl);

    if (this.containerEl) {
      this.containerEl.appendChild(cardEl);
    }

    const elements: CardElements = {
      cardEl,
      avatarEl,
      nameEl,
      jobEl,
      leaderBadgeEl,
      switchBtnEl,
      hpBarEl,
      spBarEl,
      mpBarEl,
      expBarEl,
      concEl,
      castBoxEl,
      castBarEl,
      castTextEl,
      buffsEl,
      updateTooltips: (m: EntityState) => {
        latestMemberData = m;
        if (activeTooltipType) {
          updateTipText(activeTooltipType);
        }
      },
    };

    this.cachedCards[member.id] = elements;
    return elements;
  }

  public update(player: EntityState, registry: EntityRegistry): void {
    if (!this.containerEl) return;

    // 우하단 플레이어 창은 내 캐릭터만 표시 (파티원은 파티창/그룹창에서 확인)
    const party = registry.getParty().filter((m) => m.id === player.id);
    const currentTargetId = player.targetId;

    for (let i = 0; i < party.length; i++) {
      const member = party[i];
      const card = this.ensureCard(member, i);

      const isTargeted = member.id === currentTargetId;
      const isPlayer = member.category === 'player';

      // Border and background state
      card.cardEl.style.borderColor = isTargeted ? '#38bdf8' : '#30363d';
      card.cardEl.style.backgroundColor = isTargeted ? 'rgba(14, 116, 144, 0.4)' : 'rgba(13, 17, 23, 0.85)';

      // Leader badge & switch button
      card.leaderBadgeEl.style.display = 'none'; // 파티장 표시는 파티창/그룹창에서만
      card.switchBtnEl.style.display = isPlayer ? 'none' : 'inline-block';
      if (card.nameEl.innerText !== member.name) card.nameEl.innerText = member.name;
      if (card.jobEl.innerText !== member.job) card.jobEl.innerText = member.job;
      if (card.avatarEl.innerText !== member.avatarIcon) card.avatarEl.innerText = member.avatarIcon;

      // HP Bar percentage
      const hpPct = Math.max(0, Math.min(100, (member.resources.hp / member.resources.maxHp) * 100));
      card.hpBarEl.style.width = `${hpPct}%`;
      card.hpBarEl.style.backgroundColor = hpPct > 30 ? '#22c55e' : '#ef4444';

      // SP & MP Bars
      const spPct = Math.max(0, Math.min(100, (member.resources.sp / member.resources.maxSp) * 100));
      card.spBarEl.style.width = `${spPct}%`;

      const mpPct = Math.max(0, Math.min(100, (member.resources.mp / member.resources.maxMp) * 100));
      card.mpBarEl.style.width = `${mpPct}%`;

      // EXP Bar
      const curExp = member.exp || 0;
      const maxExp = member.maxExp || Math.max(100, (member.level || 1) * 1000);
      const expPct = Math.max(0, Math.min(100, (curExp / maxExp) * 100));
      card.expBarEl.style.width = `${expPct}%`;

      // Update real-time tooltip values
      card.updateTooltips?.(member);

      // Concentration (Only for active player)
      if (isPlayer) {
        card.concEl.style.display = 'flex';
        const reserved = member.resources.maxConc - member.resources.conc;
        card.concEl.innerHTML = `<span>Conc: ${member.resources.conc}/${member.resources.maxConc}</span><span style="color:#94a3b8;">(${reserved} 보존됨)</span>`;
      } else {
        card.concEl.style.display = 'none';
      }

      // Cast bar
      if (member.cast) {
        card.castBoxEl.style.display = 'block';
        const castPct = (member.cast.progress / member.cast.castTime) * 100;
        card.castBarEl.style.width = `${castPct}%`;
        const rem = (member.cast.castTime - member.cast.progress).toFixed(1);
        card.castTextEl.innerText = `⚡ ${member.cast.skillName} (${rem}s)`;
      } else {
        card.castBoxEl.style.display = 'none';
      }

      // Buff icons
      if (member.buffs.length > 0) {
        let buffHtml = '';
        for (const b of member.buffs) {
          buffHtml += `<span title="${b.name}" style="font-size:11px; background:rgba(255,255,255,0.1); border-radius:3px; padding:0 2px;">${b.icon}</span>`;
        }
        card.buffsEl.innerHTML = buffHtml;
      } else {
        card.buffsEl.innerHTML = '';
      }
    }
  }
}
